import type { ReactNode } from 'react'
import type { CancelTransactionRequest } from '@leanpay/models'

import { useTranslation } from 'react-i18next'
import { CheckoutStep, CheckoutStepViewType } from '@leanpay/models'
import { checkoutService, logoutUser } from '@leanpay/services'
import {
    Button,
    Dialog,
    DialogClose,
    DialogContent,
    DialogTrigger,
    ThinkingFaceImg,
} from '@leanpay/ui'

import { useAppStore } from 'app/app-store'
import { isMarketSlo, redirectToDashboard, redirectToFaq } from 'library/constants'
import { useCancelTransaction } from 'library/hooks/query-hooks'

const ExitConfirmationDialog = ({ children }: { children: ReactNode }) => {
    const { t } = useTranslation()
    const { vendorToken, isExiting, setIsExiting, setIsAuthenticated } = useAppStore()
    const { mutateAsync: mutateCancelTransaction } = useCancelTransaction()

    const paramsOnEnter = {
        token: vendorToken,
        checkoutStep: CheckoutStep.CANCEL_TRANSACTION,
        checkoutStepViewType: CheckoutStepViewType.OPEN,
        data: {},
    }
    const paramsOnExit = {
        token: vendorToken,
        checkoutStep: CheckoutStep.CANCEL_TRANSACTION,
        checkoutStepViewType: CheckoutStepViewType.CLOSE,
        data: {},
    }

    const handleExitProcess = async () => {
        setIsExiting(true)
        void checkoutService.recordStep(paramsOnEnter)

        const request: CancelTransactionRequest = {
            surrogateId: null,
            token: vendorToken,
            maturityPriceId: null,
            maturityGraceId: null,
            firstPaymentDate: null,
        }
        try {
            await mutateCancelTransaction(request)
            void checkoutService.recordStep(paramsOnExit)
        } catch (error) {
            void checkoutService.recordStep({
                ...paramsOnExit,
                data: { error },
            })
        }

        // Redirect first to prevent error screen flash, then cleanup
        // SLO leaves to MyLeanpay dashboard; ROM keeps the FAQ destination
        if (isMarketSlo) {
            redirectToDashboard()
        } else {
            redirectToFaq()
        }
        logoutUser()
        setIsAuthenticated(false)
    }

    return (
        <Dialog>
            <DialogTrigger asChild>
                {children}
            </DialogTrigger>

            <DialogContent>
                <div className="p-4 pt-12 self-stretch text-center">
                    <img src={ThinkingFaceImg} alt="Thinking" className="mx-auto" />

                    <p
                        className="text-base mb-10 mt-8"
                        dangerouslySetInnerHTML={{
                            __html: t('common.cancelTransaction'),
                        }}
                    />

                    <Button
                        testId="next"
                        onClick={() => void handleExitProcess()}
                        className="mb-2"
                        isLoading={isExiting}
                        disabled={isExiting}
                    >
                        {t('common.leave')}
                    </Button>

                    <DialogClose asChild>
                        <Button testId="previous" variant="secondary" disabled={isExiting}>
                            {t('common.continue')}
                        </Button>
                    </DialogClose>
                </div>
            </DialogContent>
        </Dialog>
    )
}

export default ExitConfirmationDialog
